import { AuthorName } from '@/components/AuditDate';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DATE_TIME_FORMAT } from '@/lib/dayjs';
import type { Signature } from './signatures';
import { useSignatureImageUrl } from './useSignatureImageUrl';

/** Signature attendue de l'employé sur une absence (validée sans lui, il la signe ensuite) */
export const ABSENCE_SIGNATURE_PENDING = 'En attente de signature';

/** Tag orange d'une signature attendue */
export function PendingSignatureTag({ label = ABSENCE_SIGNATURE_PENDING }: { label?: string }) {
  return (
    <span className="inline-block whitespace-nowrap text-xs px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">
      {label}
    </span>
  );
}

/**
 * Une signature : la date, et le signataire dessous ; sinon « — » (étape qui n'a pas eu lieu), ou le tag pending
 * (signature attendue : absence pas encore signée par l'employé). Rien pendant le chargement.
 * preview (listes) : un clic montre son image dessinée dans un popover, chargée à l'ouverture seulement (une liste ne
 * charge aucune image d'avance). Sans preview : le détail d'une feuille, qui affiche l'image dessous (SignatureImage).
 */
export function SignatureCell({
  signature,
  loading = false,
  pending,
  preview = true,
}: {
  signature: Signature | null | undefined;
  loading?: boolean;
  pending?: string;
  preview?: boolean;
}) {
  if (loading) return null;
  if (!signature) {
    return pending ? <PendingSignatureTag label={pending} /> : <span className="text-slate-400">—</span>;
  }

  const text = (
    <>
      <span className="block text-slate-900">{signature.at.format(DATE_TIME_FORMAT)}</span>
      <span className="block text-xs text-slate-500">
        <AuthorName by={signature.by} />
      </span>
    </>
  );
  if (!preview) return <div className="text-sm whitespace-nowrap">{text}</div>;

  return (
    <Popover>
      <PopoverTrigger
        className="text-left text-sm whitespace-nowrap rounded-md -mx-1 px-1 hover:bg-slate-100 cursor-pointer outline-none focus-visible:ring-[3px] focus-visible:ring-blue-500/30"
        title="Voir la signature"
        // La ligne d'une liste peut ouvrir le détail de la feuille : le clic ne va qu'à la signature
        onClick={(event) => event.stopPropagation()}
      >
        {text}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80" onClick={(event) => event.stopPropagation()}>
        <SignaturePreview signature={signature} />
      </PopoverContent>
    </Popover>
  );
}

/** Contenu du popover : monté à l'ouverture, il charge alors l'image (adresse temporaire redemandée à chaque fois) */
function SignaturePreview({ signature }: { signature: Signature }) {
  const { url, loading } = useSignatureImageUrl(signature.urn);

  return (
    <div className="space-y-2">
      <div className="h-24 flex items-center justify-center rounded-lg border border-slate-200 bg-slate-50 dark:bg-white">
        {url ? (
          <img src={url} alt="Signature dessinée" className="max-h-20 max-w-full object-contain" />
        ) : (
          <span className="text-xs text-slate-500">{loading ? 'Chargement…' : "Pas d'image pour cette signature"}</span>
        )}
      </div>
      <p className="text-xs text-slate-500">
        Signée le {signature.at.format(DATE_TIME_FORMAT)} par <AuthorName by={signature.by} />
      </p>
    </div>
  );
}
